import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Checkbox,
  Dialog,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core'
import { Close as CloseIcon, Search as SearchIcon } from '@material-ui/icons'
import ResultButton from 'src/views/FileUploadView/components/ResultButton'
import CandidateRow from './components/CandidateRow'
import { Muted, Segmented, TextLink } from './components/ui'
import { candidateFitsWorkflow, candidateInfo, isCandidateAnalysed, vcfKindOf } from './model/candidates'
import { fileNamesOf, looseKey } from './model/samples'
import { INPUT_LABEL } from './model/workflow'

// "Choose uploaded files": search, a type filter and multi-select over the file
// list, grouped into samples. It opens on the file type of the batch's input, and
// each type says how many files it has (analysed ones left out while they are
// hidden); VCFs can be narrowed to one kind. Rows that cannot be analysed are
// disabled with the reason. A pasted list of sample IDs (a weekly batch from a
// sheet) finds each one. Files of another type can still be picked: the panel sets
// them aside for a batch of their own, and the footer says so.

const TYPE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'fastq', label: 'FASTQ' },
  { value: 'vcf', label: 'VCF' },
  { value: 'ont_bam', label: 'ONT BAM' },
  { value: 'pacbio_bam', label: 'PacBio BAM' },
]

const VCF_KINDS = [
  { value: 'all', label: 'All kinds' },
  { value: 'snp_indel', label: 'SNV/Indel' },
  { value: 'cnv', label: 'CNV' },
  { value: 'sv', label: 'SV' },
]

// The /file/list type each workflow input reads.
export const FILE_TYPE_OF_INPUT = { fastq: 'fastq', ont: 'ont_bam', pacbio: 'pacbio_bam', vcf: 'vcf' }

// What the batch takes, for the note on other types: "PacBio BAM", "SNV/Indel VCF".
const batchLabelOf = (workflow) =>
  workflow?.analysis === 'somatic' && workflow?.input === 'vcf' ? 'SNV/Indel VCF' : INPUT_LABEL[workflow?.input] || ''

/** "2 files are not PacBio BAM. They go to a separate batch." or '' */
export const otherTypeNote = (selected, workflow) => {
  if (!workflow) return ''
  const others = selected.filter((c) => !candidateFitsWorkflow(c, workflow)).length
  if (!others) return ''
  const label = batchLabelOf(workflow)
  return others === 1
    ? `1 file is not ${label}. It goes to a separate batch.`
    : `${others} files are not ${label}. They go to a separate batch.`
}

const MAX_ROWS = 300
const NOT_FOUND_SHOWN = 6

/**
 * A pasted list of sample IDs: 2 or more tokens split on spaces, commas, semicolons,
 * tabs or new lines, each once (case- and separator-insensitive). Null for an
 * ordinary search.
 * @returns {Array<{ id: string, key: string }> | null}
 */
export const parseIdList = (text) => {
  const tokens = String(text || '')
    .split(/[\s,;]+/)
    .filter(Boolean)
  if (tokens.length < 2) return null
  const seen = new Set()
  const ids = []
  tokens.forEach((id) => {
    const key = looseKey(id)
    if (!key || seen.has(key)) return
    seen.add(key)
    ids.push({ id, key })
  })
  return ids.length ? ids : null
}

/**
 * The candidates whose sample ID equals one of the pasted IDs (looseKey), in the
 * pasted order, and the IDs that match none.
 * @returns {{ matches: Array, notFound: string[] }}
 */
export const matchIdList = (ids, candidates) => {
  const byKey = new Map()
  candidates.forEach((c) => {
    const key = looseKey(c.key)
    if (!key) return
    if (!byKey.has(key)) byKey.set(key, [])
    byKey.get(key).push(c)
  })
  const matches = []
  const notFound = []
  ids.forEach(({ id, key }) => {
    const found = byKey.get(key)
    if (found) matches.push(...found)
    else notFound.push(id)
  })
  return { matches, notFound }
}

/**
 * The matches "Select N matches" selects: one upload per sample ID and file type,
 * the newest, that is not analysed yet. An ID whose sample of that type is already
 * in the batch is skipped, so a second upload of it is not added by accident.
 */
export const pickMatches = (selectable, matches, { analysed, inBatch }) => {
  const slot = (c) => `${looseKey(c.key)}|${c.fileType}`
  const taken = new Set(matches.filter((c) => inBatch.has(c.id)).map(slot))
  const best = new Map()
  selectable.forEach((c) => {
    const s = slot(c)
    if (taken.has(s) || isCandidateAnalysed(c, analysed)) return
    const prev = best.get(s)
    if (!prev || candidateInfo(c).uploadedAt > candidateInfo(prev).uploadedAt) best.set(s, c)
  })
  return Array.from(best.values()).slice(0, MAX_ROWS)
}

// Multi-line text pasted into the one-line search keeps its separators: browsers
// would otherwise drop the new lines and join the IDs.
const flattenPaste = (text) =>
  text
    .split(/[\r\n\t]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .join(', ')

const Mono = ({ children }) => (
  <Box component="span" sx={{ fontFamily: 'fontFamilyMono', fontSize: 12.5 }}>
    {children}
  </Box>
)

const IdListStatus = ({ total, notFound, analysedCount }) => {
  const shown = notFound.slice(0, NOT_FOUND_SHOWN)
  const more = notFound.length - shown.length
  return (
    <>
      {total - notFound.length} of {total} found
      {analysedCount ? `, ${analysedCount} already analysed` : ''}.
      {notFound.length > 0 && (
        <>
          {' '}
          Not found:{' '}
          {shown.map((id, i) => (
            <React.Fragment key={id}>
              {i ? ', ' : ''}
              <Mono>{id}</Mono>
            </React.Fragment>
          ))}
          {more > 0 ? ` and ${more} more` : ''}
        </>
      )}
    </>
  )
}

const FilePickerDialog = ({
  open,
  onClose,
  candidates,
  analysed,
  inBatch,
  onAdd,
  workflow,
  resolveWorkflow,
  initialType: initialTypeProp,
}) => {
  const initialType = initialTypeProp || FILE_TYPE_OF_INPUT[workflow?.input] || 'all'
  // Somatic runs take SNV/Indel VCFs only.
  const initialKind = workflow?.analysis === 'somatic' && initialType === 'vcf' ? 'snp_indel' : 'all'
  const [search, setSearch] = useState('')
  const [type, setType] = useState(initialType)
  const [vcfKind, setVcfKind] = useState(initialKind)
  const [hideAnalysed, setHideAnalysed] = useState(true)
  const [selected, setSelected] = useState(() => new Map())

  useEffect(() => {
    if (open) {
      setSelected(new Map())
      setSearch('')
      setType(initialType)
      setVcfKind(initialKind)
    }
  }, [open, initialType, initialKind])

  const kindMatches = (c) => type !== 'vcf' || vcfKind === 'all' || vcfKindOf(c) === vcfKind
  const hiddenAsAnalysed = (c) => hideAnalysed && isCandidateAnalysed(c, analysed) && !inBatch.has(c.id)

  // Files per type, as the list would show them without a search.
  const counts = useMemo(() => {
    const out = { all: 0 }
    candidates.forEach((c) => {
      if (hiddenAsAnalysed(c)) return
      out.all += 1
      out[c.fileType] = (out[c.fileType] || 0) + 1
    })
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates, hideAnalysed, analysed, inBatch])
  // A somatic workflow takes FASTQ or VCF only: long-read BAM types are not offered.
  const typeOptions = TYPE_FILTERS.filter(
    (o) => workflow?.analysis !== 'somatic' || !['ont_bam', 'pacbio_bam'].includes(o.value),
  ).map((o) => ({ ...o, label: `${o.label} ${counts[o.value] || 0}` }))

  const idList = useMemo(() => parseIdList(search), [search])

  // With pasted IDs the list is their matches, analysed ones included: they were
  // asked for by name, and each row says it is analysed.
  const idMatch = useMemo(() => {
    if (!idList) return null
    const pool = candidates.filter((c) => (type === 'all' || c.fileType === type) && kindMatches(c))
    return matchIdList(idList, pool)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idList, candidates, type, vcfKind])

  const filtered = useMemo(() => {
    if (idMatch) return idMatch.matches
    const q = search.trim().toLowerCase()
    return candidates.filter((c) => {
      if (type !== 'all' && c.fileType !== type) return false
      if (!kindMatches(c)) return false
      if (hiddenAsAnalysed(c)) return false
      if (!q) return true
      if (c.key.toLowerCase().includes(q)) return true
      return c.rows.some((row) => fileNamesOf(row).some((n) => n.toLowerCase().includes(q)))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idMatch, candidates, type, vcfKind, search, hideAnalysed, analysed, inBatch])

  // Files the list would show if analysed ones were not hidden: an empty list
  // must say they exist, or a fully analysed organisation looks like it has no
  // uploads at all.
  const hiddenAnalysedCount = useMemo(() => {
    if (idMatch || !hideAnalysed) return 0
    const q = search.trim().toLowerCase()
    return candidates.filter((c) => {
      if (type !== 'all' && c.fileType !== type) return false
      if (!kindMatches(c)) return false
      if (!hiddenAsAnalysed(c)) return false
      if (!q) return true
      if (c.key.toLowerCase().includes(q)) return true
      return c.rows.some((row) => fileNamesOf(row).some((n) => n.toLowerCase().includes(q)))
    }).length
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idMatch, candidates, type, vcfKind, search, hideAnalysed, analysed, inBatch])

  const toggle = (candidate) =>
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(candidate.id)) next.delete(candidate.id)
      else next.set(candidate.id, candidate)
      return next
    })

  const selectable = filtered.filter((c) => !inBatch.has(c.id) && !candidateInfo(c).disabledReason)
  const allSelected = selectable.length > 0 && selectable.every((c) => selected.has(c.id))
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Map(prev)
      if (allSelected) selectable.forEach((c) => next.delete(c.id))
      else selectable.slice(0, MAX_ROWS).forEach((c) => next.set(c.id, c))
      return next
    })

  // "Select N matches" leaves analysed samples to a deliberate click, since running
  // one again is charged again.
  const matchesToSelect = idMatch ? pickMatches(selectable, idMatch.matches, { analysed, inBatch }) : []
  const analysedMatches = idMatch ? idMatch.matches.filter((c) => isCandidateAnalysed(c, analysed)).length : 0
  const selectMatches = () =>
    setSelected((prev) => {
      const next = new Map(prev)
      matchesToSelect.forEach((c) => next.set(c.id, c))
      return next
    })

  const onPaste = (e) => {
    const text = e.clipboardData?.getData('text') || ''
    if (!/[\r\n\t]/.test(text)) return
    e.preventDefault()
    const { selectionStart, selectionEnd } = e.target
    const start = typeof selectionStart === 'number' ? selectionStart : search.length
    const end = typeof selectionEnd === 'number' ? selectionEnd : search.length
    const before = search.slice(0, start)
    const after = search.slice(end)
    const joiner = (edge) => (edge && !/[\s,;]$|^[\s,;]/.test(edge) ? ', ' : '')
    setSearch(`${before}${joiner(before.slice(-1))}${flattenPaste(text)}${joiner(after.charAt(0))}${after}`)
  }

  const count = selected.size
  // The batch this selection would join: while the batch is empty the selection
  // itself decides the input (resolveWorkflow), so the note names the rest.
  const picked = Array.from(selected.values())
  const note = otherTypeNote(picked, resolveWorkflow && picked.length ? resolveWorkflow(picked) : workflow)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: '12px', height: 'min(760px, calc(100% - 64px))', m: 2 } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', px: 3, pt: 2.5, pb: 1 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, flex: 1 }}>Choose uploaded files</Typography>
        <IconButton onClick={onClose} aria-label="Close" size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 1.5, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          autoFocus
          placeholder="Search, or paste sample IDs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputProps={{ 'aria-label': 'Search, or paste sample IDs', onPaste }}
          sx={{ width: 240, '& .MuiInputBase-root': { fontSize: 14 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        <Segmented
          ariaLabel="File type"
          value={type}
          options={typeOptions}
          onChange={setType}
          fullWidth={false}
          size="small"
        />
        {type === 'vcf' && (
          <Segmented
            ariaLabel="VCF kind"
            value={vcfKind}
            options={VCF_KINDS}
            onChange={setVcfKind}
            fullWidth={false}
            size="small"
          />
        )}
        {!idMatch && (
          <FormControlLabel
            control={
              <Checkbox size="small" checked={hideAnalysed} onChange={(e) => setHideAnalysed(e.target.checked)} />
            }
            label={<Typography sx={{ fontSize: 13.5 }}>Hide analysed</Typography>}
            sx={{ ml: 0 }}
          />
        )}
      </Box>
      {idMatch && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1.5, px: 3, pb: 1 }}>
          <Muted>
            <IdListStatus total={idList.length} notFound={idMatch.notFound} analysedCount={analysedMatches} />
          </Muted>
          {matchesToSelect.some((c) => !selected.has(c.id)) && (
            <TextLink onClick={selectMatches}>
              Select {matchesToSelect.length} {matchesToSelect.length === 1 ? 'match' : 'matches'}
            </TextLink>
          )}
        </Box>
      )}
      <Box sx={{ px: 2, display: 'flex', alignItems: 'center', minHeight: 36 }}>
        <FormControlLabel
          control={<Checkbox size="small" checked={allSelected} onChange={toggleAll} disabled={!selectable.length} />}
          label={<Muted>Select all shown</Muted>}
          sx={{ ml: 0.25 }}
        />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2 }}>
        {filtered.length === 0 && hiddenAnalysedCount === 0 && <Muted sx={{ px: 1, py: 3 }}>No files match.</Muted>}
        {filtered.length === 0 && hiddenAnalysedCount > 0 && (
          <Box sx={{ px: 1, py: 3 }} data-testid="picker-all-analysed">
            <Muted>
              {hiddenAnalysedCount === 1
                ? 'The matching file is already analysed and hidden.'
                : `All ${hiddenAnalysedCount} matching files are already analysed and hidden.`}{' '}
            </Muted>
            <TextLink onClick={() => setHideAnalysed(false)}>Show analysed files</TextLink>
          </Box>
        )}
        {filtered.slice(0, MAX_ROWS).map((c) => (
          <CandidateRow
            key={c.id}
            candidate={c}
            selectable
            checked={selected.has(c.id)}
            added={inBatch.has(c.id)}
            analysed={isCandidateAnalysed(c, analysed)}
            onToggle={toggle}
          />
        ))}
        {filtered.length > MAX_ROWS && (
          <Muted sx={{ px: 1, py: 2 }}>
            Showing {MAX_ROWS} of {filtered.length}. Search to narrow the list.
          </Muted>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5, px: 3, py: 2 }}>
        {note && <Muted sx={{ mr: 'auto' }}>{note}</Muted>}
        <ResultButton onClick={onClose} sx={{ height: 36, px: 2, fontSize: 14 }}>
          Cancel
        </ResultButton>
        <ResultButton
          primary
          disabled={!count}
          onClick={() => onAdd(Array.from(selected.values()))}
          sx={{ height: 36, px: 2, fontSize: 14 }}
        >
          {count ? `Add ${count} ${count === 1 ? 'sample' : 'samples'}` : 'Add samples'}
        </ResultButton>
      </Box>
    </Dialog>
  )
}

export default FilePickerDialog
